import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useCatalogSubscription } from '../../hooks/useCatalogSubscription';
import {
  useAddLocalMCP,
  useControlMCP,
  useDeleteMCP,
  useLocalMcpDefinition,
  useMCPServers,
  useMCPTools,
  useRegisterMCP,
  useUpdateLocalMCP,
  useUpdateMCP,
} from '../../hooks/useToolsManagement';
import {
  lifecycleActions,
  mcpCommandErrorMessage,
  observedStateLabel,
  overriddenMessage,
  type ToolsServerRow,
} from '../../hooks/tools-servers';
import { getSDK } from '../../services/sdk-instance';
import { Button } from '@localagent/ui/components/button';
import { Badge } from '@localagent/ui/components/badge';
import { ListDetailLayout } from '@localagent/ui/components/list-detail-layout';
import { DataList, PowerIcon, PowerOffIcon, TrashIcon } from '@localagent/ui/components/data-list';
import type { ItemAction } from '@localagent/ui/components/data-list';
import { ListAddButton, ListFilterMenu } from '@localagent/ui/components/list-filter-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@localagent/ui/components/dialog';
import { Input } from '@localagent/ui/components/input';
import { Label } from '@localagent/ui/components/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@localagent/ui/components/select';
import { toast } from '@localagent/ui/components/sonner';
import { LoadingState } from '@localagent/ui/components/shogun-spinner';
import { useFilteredList, type FilterValue } from '@localagent/ui/hooks/use-filtered-list';
import { usePagination } from '@localagent/ui/hooks/use-pagination';
import { cn } from '@localagent/ui/lib/utils';
import { ShogunChopHTTPError } from '@localagent/sdk';
import type {
  MCPStatusInfo,
  McpControlAction,
  ShogunChopMcpServerDefinition,
  ToolDefinition,
  UpdateLocalMCPRequest,
} from '@localagent/sdk';

const TOOL_CHIPS: Array<{ value: '' | 'enabled' | 'disabled'; label: string; testid: string }> = [
  { value: '', label: 'All', testid: 'tool-filter-all' },
  { value: 'enabled', label: 'Enabled', testid: 'tool-filter-enabled' },
  { value: 'disabled', label: 'Disabled', testid: 'tool-filter-disabled' },
];

const STARTING_MESSAGE = 'Starting… the first run may download the package';
const DESKTOP_ONLY_MESSAGE = 'Local (stdio) servers are added from the ShogunChop desktop app, on the computer that runs them.';

const mcpSearchFn = (item: ToolsServerRow, query: string): boolean => {
  const q = query.toLowerCase();
  return item.name.toLowerCase().includes(q) || item.mcp_id.toLowerCase().includes(q);
};

function statusColor(status: string): string {
  if (status === 'available') return 'bg-success';
  if (status === 'unavailable' || status === 'error') return 'bg-error';
  return 'bg-warning';
}

export interface McpServerDetailProps {
  server: MCPStatusInfo;
  tools: ToolDefinition[];
  // (tools ADR-016)
  deviceReachable: boolean | undefined;
  computerName?: string | null;
  onLifecycle?: (action: McpControlAction) => void;
  lifecycleDisabled?: boolean;
  onToggleEnabled: () => void;
  toggleDisabled: boolean;
  onDelete: () => void;
  hasDaemon?: boolean;
  onThisComputer?: boolean;
  onEdit?: () => void;
}

const LIFECYCLE_LABELS: Record<McpControlAction, string> = { start: 'Start', stop: 'Stop', restart: 'Restart' };

export function McpServerDetail({
  server,
  tools,
  deviceReachable,
  computerName,
  onLifecycle,
  lifecycleDisabled,
  onToggleEnabled,
  toggleDisabled,
  onDelete,
  hasDaemon = false,
  onThisComputer = false,
  onEdit,
}: McpServerDetailProps) {
  const cfg = server.config;
  const stateLabel = observedStateLabel({ observed_state: server.observed_state, device_name: computerName ?? null });
  const editable = Boolean(onEdit) && hasDaemon && onThisComputer && server.transport === 'bridge';
  return (
    <div className="flex flex-col gap-4 p-6">
      <div className="flex items-center justify-between">
        <h2 className="text-title-2 font-semibold">{server.name}</h2>
        <div className="flex items-center gap-2">
          <Badge variant={server.enabled ? 'secondary' : 'outline'}>
            {server.enabled ? 'Enabled' : 'Disabled'}
          </Badge>
          <Badge variant="outline" className={cn("text-caption", statusColor(server.status).replace('bg-', 'text-'))}>
            {server.status}
          </Badge>
        </div>
      </div>
      <p className="text-body text-muted-foreground font-mono">{server.mcp_id}</p>
      {stateLabel && (
        <p className="text-body" data-testid="mcp-observed-state">
          {computerName ? `On ${computerName}: ` : ''}{stateLabel}
        </p>
      )}
      {server.observed_state === 'stopped' && (
        <p className="text-body text-muted-foreground" data-testid="mcp-stopped-explanation">
          Stopped — its tools are not offered to agents until you start it.
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline">{server.transport}</Badge>
        <Badge variant="outline">{server.location}</Badge>
        <Badge variant="outline">{server.scope}</Badge>
      </div>
      {server.error && (
        <p className="text-body text-destructive">{server.error}</p>
      )}

      {cfg && (
        <div className="rounded-md bg-card p-3 text-body">
          <h3 className="font-medium mb-1">Configuration</h3>
          {cfg.url && <p className="text-muted-foreground">URL: <span className="font-mono">{cfg.url}</span></p>}
          {cfg.command && <p className="text-muted-foreground">Command: <span className="font-mono">{cfg.command}</span></p>}
          {cfg.args && cfg.args.length > 0 && <p className="text-muted-foreground">Args: <span className="font-mono">{cfg.args.join(' ')}</span></p>}
          {cfg.env && Object.keys(cfg.env).length > 0 && <p className="text-muted-foreground">Env: {Object.keys(cfg.env).join(', ')}</p>}
          {cfg.headers && Object.keys(cfg.headers).length > 0 && <p className="text-muted-foreground">Headers: {Object.keys(cfg.headers).join(', ')}</p>}
        </div>
      )}

      {deviceReachable === false && (
        <div
          data-testid="mcp-device-unreachable"
          className="rounded-md bg-warning-subtle p-3 text-body"
        >
          <p className="font-medium">This device is not connected</p>
          <p className="text-muted-foreground">
            The server is still registered, but its tools cannot be used until the
            device reconnects. Nothing has been removed.
          </p>
        </div>
      )}

      <div className="flex items-center gap-2 text-body text-muted-foreground">
        <span data-testid="mcp-tools-active">{server.tools_count} tools active</span>
        <span>/</span>
        <span>{server.tools_total} total</span>
        {deviceReachable === false && (
          <span data-testid="mcp-usable-state" className="text-warning-text">
            · not usable
          </span>
        )}
        {deviceReachable === true && (
          <span data-testid="mcp-usable-state" className="text-muted-foreground">
            · usable
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        {onLifecycle && lifecycleActions(server.observed_state).map((action) => (
          <Button
            key={action}
            variant="outline"
            size="sm"
            data-testid={`mcp-lifecycle-${action}`}
            onClick={() => onLifecycle(action)}
            disabled={lifecycleDisabled}
          >
            {LIFECYCLE_LABELS[action]}
          </Button>
        ))}
        {editable && (
          <Button variant="outline" size="sm" data-testid="mcp-edit" onClick={onEdit}>
            Edit
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          data-testid="mcp-toggle-enabled"
          onClick={onToggleEnabled}
          disabled={toggleDisabled}
        >
          {server.enabled ? 'Disable' : 'Enable'}
        </Button>
        <Button variant="destructive" size="sm" data-testid="mcp-delete" onClick={onDelete}>
          Delete
        </Button>
      </div>
      {tools.length > 0 && (
        <div className="mt-2">
          <h3 className="text-label font-semibold mb-2">Tools ({tools.length})</h3>
          <div>
            <table className="w-full text-body">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-3 py-2 text-left font-medium">Name</th>
                  <th className="px-3 py-2 text-left font-medium">Description</th>
                </tr>
              </thead>
              <tbody>
                {tools.map(tool => (
                  <tr key={tool.function.name} className="border-b last:border-0">
                    <td className="px-3 py-2 font-mono text-caption whitespace-nowrap">{tool.function.name}</td>
                    <td className="px-3 py-2 text-caption text-muted-foreground">{tool.function.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

type KVEntry = { key: string; value: string };

function kvToRecord(entries: KVEntry[]): Record<string, string> | undefined {
  if (entries.length === 0) return undefined;
  return Object.fromEntries(entries.map((e) => [e.key.trim(), e.value]));
}

export function entriesError(entries: KVEntry[], noun: string): string | null {
  const seen = new Set<string>();
  for (const [i, entry] of entries.entries()) {
    const key = entry.key.trim();
    if (!key) return `${noun} ${i + 1} has no name — enter one or remove the row`;
    if (key.includes('=') || key.includes('\0')) return `${noun} name "${key}" cannot contain "=" or a NUL character`;
    if (seen.has(key)) return `${noun} "${key}" is listed more than once`;
    seen.add(key);
  }
  return null;
}

export function argsError(args: string[]): string | null {
  const empty = args.findIndex((arg) => arg === '');
  return empty === -1 ? null : `Argument ${empty + 1} is empty — enter a value or remove it`;
}

function FieldError({ message, testId }: { message: string | null; testId: string }) {
  if (!message) return null;
  return <p className="mt-1 text-caption text-destructive" data-testid={testId}>{message}</p>;
}

function definitionReadErrorMessage(err: Error): string {
  if (!(err instanceof ShogunChopHTTPError)) return err.message;
  const reason = (err.body as { error?: unknown } | null)?.error;
  if (typeof reason !== 'string') {
    throw new Error(`definition read failed with HTTP ${err.status} but no reason from the computer`);
  }
  return reason;
}

function KeyValueEditor({ entries, onChange, keyPlaceholder = 'Key', valuePlaceholder = 'Value', testId }: {
  entries: KVEntry[];
  onChange: (entries: KVEntry[]) => void;
  keyPlaceholder?: string;
  valuePlaceholder?: string;
  testId?: string;
}) {
  return (
    <div className="flex flex-col gap-1.5" data-testid={testId}>
      {entries.map((entry, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <Input
            value={entry.key}
            onChange={(e) => {
              const next = [...entries];
              next[i] = { ...entry, key: e.target.value };
              onChange(next);
            }}
            placeholder={keyPlaceholder}
            aria-label={`${keyPlaceholder} ${i + 1}`}
            className="flex-1 h-8 text-caption"
          />
          <Input
            value={entry.value}
            onChange={(e) => {
              const next = [...entries];
              next[i] = { ...entry, value: e.target.value };
              onChange(next);
            }}
            placeholder={valuePlaceholder}
            aria-label={`${valuePlaceholder} ${i + 1}`}
            className="flex-1 h-8 text-caption"
          />
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 shrink-0"
            aria-label={`Remove ${entry.key || `row ${i + 1}`}`}
            onClick={() => onChange(entries.filter((_, j) => j !== i))}
          >
            x
          </Button>
        </div>
      ))}
      <Button
        variant="outline"
        size="sm"
        className="self-start h-7 text-caption"
        onClick={() => onChange([...entries, { key: '', value: '' }])}
      >
        + Add
      </Button>
    </div>
  );
}

function ArgsEditor({ args, onChange }: { args: string[]; onChange: (args: string[]) => void }) {
  return (
    <div className="flex flex-col gap-1.5">
      {args.map((arg, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <Input
            value={arg}
            onChange={(e) => {
              const next = [...args];
              next[i] = e.target.value;
              onChange(next);
            }}
            placeholder={`Argument ${i + 1}`}
            aria-label={`Argument ${i + 1}`}
            className="flex-1 h-8 text-caption"
          />
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 shrink-0"
            aria-label={`Remove argument ${i + 1}`}
            onClick={() => onChange(args.filter((_, j) => j !== i))}
          >
            x
          </Button>
        </div>
      ))}
      <Button
        variant="outline"
        size="sm"
        className="self-start h-7 text-caption"
        onClick={() => onChange([...args, ''])}
      >
        + Add argument
      </Button>
    </div>
  );
}

type RegisterTransport = 'bridge' | 'http';

export interface RegisterMcpDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hasDaemon: boolean;
  onRegistered: (mcpId: string) => void;
}

export function RegisterMcpDialog({ open, onOpenChange, hasDaemon, onRegistered }: RegisterMcpDialogProps) {
  const registerMcpMutation = useRegisterMCP();
  const addLocalMutation = useAddLocalMCP();
  const [transport, setTransport] = useState<RegisterTransport>(hasDaemon ? 'bridge' : 'http');
  const [mcpId, setMcpId] = useState('');
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [command, setCommand] = useState('');
  const [args, setArgs] = useState<string[]>([]);
  const [env, setEnv] = useState<KVEntry[]>([]);
  const [headers, setHeaders] = useState<KVEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { reset: resetRegister } = registerMcpMutation;
  const { reset: resetAddLocal } = addLocalMutation;

  useEffect(() => {
    if (!open) return;
    resetRegister();
    resetAddLocal();
    setTransport(hasDaemon ? 'bridge' : 'http');
    setMcpId('');
    setName('');
    setUrl('');
    setCommand('');
    setArgs([]);
    setEnv([]);
    setHeaders([]);
    setError(null);
  }, [open, hasDaemon, resetRegister, resetAddLocal]);

  const local = transport === 'bridge';
  const pending = registerMcpMutation.isPending || addLocalMutation.isPending;
  const argsProblem = local ? argsError(args) : null;
  const envProblem = local ? entriesError(env, 'Variable') : null;
  const headersProblem = local ? null : entriesError(headers, 'Header');
  const canSubmit = local
    ? Boolean(name.trim() && command.trim()) && !argsProblem && !envProblem
    : Boolean(mcpId.trim() && name.trim() && url.trim()) && !headersProblem;

  const submit = () => {
    setError(null);
    if (local) {
      addLocalMutation.mutate(
        { name: name.trim(), command: command.trim(), args, env: kvToRecord(env) },
        {
          onSuccess: (server) => {
            onOpenChange(false);
            onRegistered(server.id);
            toast.success(`Added "${server.name}" (${server.tool_names.length} tools)`);
          },
          onError: (err) => setError(mcpCommandErrorMessage(err, true)),
        },
      );
      return;
    }
    registerMcpMutation.mutate(
      { mcp_id: mcpId.trim(), name: name.trim(), transport: 'http', config: { url: url.trim(), headers: kvToRecord(headers) } },
      {
        onSuccess: (res) => {
          onOpenChange(false);
          onRegistered(res.mcp_id);
          toast.success(`Registered "${res.mcp_id}" (${res.tools_discovered} tools discovered)`);
        },
        onError: (err) => setError(mcpCommandErrorMessage(err, false)),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="mcp-register-dialog">
        <DialogHeader>
          <DialogTitle>Register MCP Server</DialogTitle>
          <DialogDescription>Register a new MCP server to make its tools available to agents.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3 py-2">
          <div>
            <Label>Transport</Label>
            <Select value={transport} onValueChange={(v) => setTransport(v as RegisterTransport)}>
              <SelectTrigger data-testid="mcp-register-transport">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {hasDaemon && <SelectItem value="bridge">Local (stdio)</SelectItem>}
                <SelectItem value="http">Remote (HTTP/SSE)</SelectItem>
              </SelectContent>
            </Select>
            {!hasDaemon && (
              <p className="mt-1 text-caption text-muted-foreground" data-testid="mcp-register-desktop-only">
                {DESKTOP_ONLY_MESSAGE}
              </p>
            )}
          </div>
          {!local && (
            <div>
              <Label htmlFor="reg-mcp-id">MCP ID</Label>
              <Input id="reg-mcp-id" value={mcpId} onChange={(e) => setMcpId(e.target.value)} placeholder="my-custom-mcp" />
            </div>
          )}
          <div>
            <Label htmlFor="reg-mcp-name">Name</Label>
            <Input
              id="reg-mcp-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={local ? 'chrome-devtools' : 'My Custom MCP'}
            />
          </div>
          {local ? (
            <>
              <div>
                <Label htmlFor="reg-command">Command</Label>
                <Input id="reg-command" value={command} onChange={(e) => setCommand(e.target.value)} placeholder="npx" />
              </div>
              <div>
                <Label>Arguments</Label>
                <ArgsEditor args={args} onChange={setArgs} />
                <FieldError message={argsProblem} testId="mcp-register-args-error" />
              </div>
              <div>
                <Label>Environment Variables</Label>
                <KeyValueEditor entries={env} onChange={setEnv} keyPlaceholder="VAR_NAME" valuePlaceholder="value" testId="mcp-register-env" />
                <FieldError message={envProblem} testId="mcp-register-env-error" />
              </div>
              <p className="text-caption text-muted-foreground italic">Runs on this computer. Environment values stay on this computer.</p>
            </>
          ) : (
            <>
              <div>
                <Label htmlFor="reg-url">URL</Label>
                <Input id="reg-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="http://localhost:8080/sse" />
              </div>
              <div>
                <Label>Headers</Label>
                <KeyValueEditor entries={headers} onChange={setHeaders} keyPlaceholder="Header name" valuePlaceholder="Value" />
                <FieldError message={headersProblem} testId="mcp-register-headers-error" />
              </div>
              <p className="text-caption text-muted-foreground italic">This MCP will be scoped to: your organization</p>
            </>
          )}
          {local && addLocalMutation.isPending && (
            <p className="text-caption text-muted-foreground" data-testid="mcp-register-starting">{STARTING_MESSAGE}</p>
          )}
          {error && (
            <p className="text-caption text-destructive" data-testid="mcp-register-error">{error}</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="mcp-register-submit" onClick={submit} disabled={!canSubmit || pending}>
            {pending ? (local ? 'Starting...' : 'Registering...') : (local ? 'Add' : 'Register')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface EditDraft {
  command: string;
  args: string[];
  env: KVEntry[];
}

export function editRequest(loaded: ShogunChopMcpServerDefinition, draft: EditDraft): UpdateLocalMCPRequest {
  const request: UpdateLocalMCPRequest = {};
  const command = draft.command.trim();
  if (command !== loaded.command) request.command = command;
  const args = draft.args;
  if (args.length !== loaded.args.length || args.some((a, i) => a !== loaded.args[i])) request.args = args;
  const next = kvToRecord(draft.env) ?? {};
  const envSet: Record<string, string> = {};
  for (const [k, v] of Object.entries(next)) {
    if (loaded.env[k] !== v) envSet[k] = v;
  }
  if (Object.keys(envSet).length > 0) request.env_set = envSet;
  const envRemove = Object.keys(loaded.env).filter((k) => !(k in next));
  if (envRemove.length > 0) request.env_remove = envRemove;
  return request;
}

export interface EditLocalMcpFormProps {
  definition: ShogunChopMcpServerDefinition;
  pending: boolean;
  error: string | null;
  onSubmit: (request: UpdateLocalMCPRequest) => void;
  onCancel: () => void;
}

export function EditLocalMcpForm({ definition, pending, error, onSubmit, onCancel }: EditLocalMcpFormProps) {
  const [command, setCommand] = useState(definition.command);
  const [args, setArgs] = useState<string[]>(definition.args);
  const [env, setEnv] = useState<KVEntry[]>(Object.entries(definition.env).map(([key, value]) => ({ key, value })));
  const argsProblem = argsError(args);
  const envProblem = entriesError(env, 'Variable');
  const request = editRequest(definition, { command, args, env });
  const changed = Object.keys(request).length > 0;
  return (
    <div className="flex flex-col gap-3" data-testid="mcp-edit-form">
      <div>
        <Label htmlFor="edit-command">Command</Label>
        <Input id="edit-command" value={command} onChange={(e) => setCommand(e.target.value)} />
      </div>
      <div>
        <Label>Arguments</Label>
        <ArgsEditor args={args} onChange={setArgs} />
        <FieldError message={argsProblem} testId="mcp-edit-args-error" />
      </div>
      <div>
        <Label>Environment Variables</Label>
        <KeyValueEditor entries={env} onChange={setEnv} keyPlaceholder="VAR_NAME" valuePlaceholder="value" testId="mcp-edit-env" />
        <FieldError message={envProblem} testId="mcp-edit-env-error" />
      </div>
      {pending && (
        <p className="text-caption text-muted-foreground" data-testid="mcp-edit-starting">{STARTING_MESSAGE}</p>
      )}
      {error && (
        <p className="text-caption text-destructive" data-testid="mcp-edit-error">{error}</p>
      )}
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button
          data-testid="mcp-edit-submit"
          disabled={!changed || pending || !command.trim() || argsProblem !== null || envProblem !== null}
          onClick={() => onSubmit(request)}
        >
          {pending ? 'Saving...' : 'Save'}
        </Button>
      </DialogFooter>
    </div>
  );
}

function EditLocalMcpDialog({ target, onClose }: { target: { mcpId: string; name: string } | null; onClose: () => void }) {
  const definition = useLocalMcpDefinition(target?.mcpId ?? null);
  const updateMutation = useUpdateLocalMCP();
  const [error, setError] = useState<string | null>(null);
  const { reset: resetUpdate } = updateMutation;
  const openMcpId = target?.mcpId ?? null;

  useEffect(() => {
    if (openMcpId === null) return;
    resetUpdate();
    setError(null);
  }, [openMcpId, resetUpdate]);

  return (
    <Dialog open={target !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="mcp-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit {target?.name}</DialogTitle>
          <DialogDescription>Changes apply on this computer. A running server restarts with them.</DialogDescription>
        </DialogHeader>
        {definition.isLoading && (
          <LoadingState variant="inline" label="Reading the server's definition from this computer…" data-testid="mcp-edit-loading" />
        )}
        {definition.error && (
          <p className="text-caption text-destructive" data-testid="mcp-edit-error">
            {definitionReadErrorMessage(definition.error)}
          </p>
        )}
        {definition.data && target && (
          <EditLocalMcpForm
            key={target.mcpId}
            definition={definition.data}
            pending={updateMutation.isPending}
            error={error}
            onCancel={onClose}
            onSubmit={(request) => {
              setError(null);
              updateMutation.mutate(
                { mcpId: target.mcpId, request },
                {
                  onSuccess: (reply) => {
                    if (reply.outcome === 'queued') {
                      toast(`${target.name}: the change will apply once the server's earlier command finishes`);
                    } else if (reply.outcome === 'superseded') {
                      toast.success(`${target.name}: saved. A later command left it ${reply.observed_state}`);
                    } else {
                      toast.success(`${target.name} ${reply.observed_state}`);
                    }
                    onClose();
                  },
                  onError: (err) => setError(mcpCommandErrorMessage(err, true)),
                },
              );
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

export interface McpServersPanelState {
  list: ReactNode;
  detail: ReactNode | undefined;
  selectedId: string | null;
  setSelectedId: (id: string | null) => void;
  dialogs: ReactNode;
}

export function useMcpServersPanel(): McpServersPanelState {
  useCatalogSubscription('mcp');
  const hasDaemon = getSDK().hasDaemon;
  const [selectedMcpId, setSelectedMcpId] = useState<string | null>(null);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [mcpToDelete, setMcpToDelete] = useState<ToolsServerRow | null>(null);
  const [editTarget, setEditTarget] = useState<{ mcpId: string; name: string } | null>(null);

  const { data: mcpServersData, isLoading: mcpServersLoading } = useMCPServers();
  const { data: mcpToolsData } = useMCPTools(selectedMcpId);
  const updateMcpMutation = useUpdateMCP();
  const deleteMcpMutation = useDeleteMCP();
  const controlMcpMutation = useControlMCP();

  const mcpServers = useMemo(() => mcpServersData?.rows ?? [], [mcpServersData]);
  const selectedMcp = useMemo(
    () => mcpServers.find(m => m.mcp_id === selectedMcpId) ?? null,
    [mcpServers, selectedMcpId],
  );
  const deviceReachable = selectedMcp?.transport === 'bridge'
    ? selectedMcp.observed_state !== 'unknown'
    : mcpServersData?.device_reachable;

  const toolFilterFns: Record<string, (item: ToolsServerRow, value: FilterValue) => boolean> = useMemo(() => ({
    status: (item, value) => {
      if (value === 'enabled') return item.enabled;
      if (value === 'disabled') return !item.enabled;
      return true;
    },
  }), []);

  const {
    filteredItems: filteredMcpServers,
    searchValue: mcpSearchValue,
    onSearchChange: mcpOnSearchChange,
    filterValues: mcpFilterValues,
    onFilterChange: mcpOnFilterChange,
  } = useFilteredList({ items: mcpServers, searchFn: mcpSearchFn, filterFns: toolFilterFns });

  const mcpStatusScope = (mcpFilterValues.status as string | undefined) ?? '';

  const mcpPagination = usePagination({
    totalItems: filteredMcpServers.length,
    pageSize: 10,
    pageSizeOptions: [10, 25, 50, 100],
  });

  useEffect(() => {
    mcpPagination.reset();
  }, [mcpSearchValue, mcpStatusScope]); // eslint-disable-line react-hooks/exhaustive-deps

  const pagedMcpServers = filteredMcpServers.slice(mcpPagination.startIndex, mcpPagination.endIndex);

  const getMcpItemId = useCallback((item: ToolsServerRow) => item.mcp_id, []);
  const handleMcpItemClick = useCallback((item: ToolsServerRow) => {
    setSelectedMcpId(item.mcp_id);
  }, []);

  const mcpItemActions = useCallback((item: ToolsServerRow): ItemAction[] => [
    {
      label: item.enabled ? 'Disable' : 'Enable',
      icon: item.enabled ? <PowerOffIcon /> : <PowerIcon />,
      testId: `tool-toggle-${item.mcp_id}`,
      onClick: () => {
        updateMcpMutation.mutate(
          { deviceId: item.device_id, mcpId: item.mcp_id, request: { enabled: !item.enabled } },
          {
            onSuccess: () => toast.success(`${item.name} ${item.enabled ? 'disabled' : 'enabled'}`),
            onError: (err) => toast.error(err.message),
          },
        );
      },
    },
    {
      label: 'Delete',
      icon: <TrashIcon />,
      variant: 'destructive',
      onClick: () => setMcpToDelete(item),
    },
  ], [updateMcpMutation]);

  const handleDeleteMcp = useCallback(() => {
    if (!mcpToDelete) return;
    deleteMcpMutation.mutate({ deviceId: mcpToDelete.device_id, mcpId: mcpToDelete.mcp_id }, {
      onSuccess: () => {
        setMcpToDelete(null);
        if (selectedMcpId === mcpToDelete.mcp_id) setSelectedMcpId(null);
        toast.success(`Deleted "${mcpToDelete.mcp_id}"`);
      },
      onError: (err) => toast.error(mcpCommandErrorMessage(err, mcpToDelete.on_this_computer)),
    });
  }, [mcpToDelete, deleteMcpMutation, selectedMcpId]);

  const renderMcpItem = useCallback((item: ToolsServerRow) => (
    <div
      data-testid={`tool-item-${item.mcp_id}`}
      className={cn(
        "flex items-center gap-3 rounded-lg p-3 pr-20 transition-colors hover:bg-accent/50 cursor-pointer",
        selectedMcpId === item.mcp_id && "bg-accent",
      )}
    >
      <div
        data-testid={`tool-count-${item.mcp_id}`}
        className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-label font-semibold"
      >
        {item.tools_count}
        <span className={cn("absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-background", statusColor(item.status))} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-label font-medium">{item.name}</span>
          <Badge variant="outline" className="text-micro">{item.transport}</Badge>
          <Badge variant="outline" className="text-micro">{item.scope}</Badge>
          <Badge variant={item.enabled ? 'secondary' : 'outline'} className="text-micro">
            {item.enabled ? 'enabled' : 'disabled'}
          </Badge>
        </div>
        <p className="truncate text-caption text-muted-foreground font-mono">{item.mcp_id}</p>
        {item.device_name && (
          <p className="truncate text-caption text-muted-foreground" data-testid={`tool-computer-${item.mcp_id}`}>
            {item.on_this_computer ? `${item.device_name} (this computer)` : item.device_name}
            {observedStateLabel(item) && <span data-testid={`tool-state-${item.mcp_id}`}> · {observedStateLabel(item)}</span>}
          </p>
        )}
        {item.error && (
          <p className="truncate text-caption text-destructive">{item.error}</p>
        )}
      </div>
    </div>
  ), [selectedMcpId]);

  const detail = useMemo(() => {
    if (!selectedMcp) return undefined;
    return (
      <McpServerDetail
        server={selectedMcp}
        tools={mcpToolsData?.tools ?? []}
        deviceReachable={deviceReachable}
        computerName={selectedMcp.device_name}
        lifecycleDisabled={controlMcpMutation.isPending}
        onLifecycle={(action) => {
          controlMcpMutation.mutate(
            { deviceId: selectedMcp.device_id, mcpId: selectedMcp.mcp_id, action },
            {
              onSuccess: (reply) => {
                if (reply.outcome === 'overridden') toast(overriddenMessage(reply.observed_state));
                else toast.success(`${selectedMcp.name} ${reply.observed_state}`);
              },
              onError: (err) => toast.error(mcpCommandErrorMessage(err, selectedMcp.on_this_computer)),
            },
          );
        }}
        toggleDisabled={updateMcpMutation.isPending}
        onToggleEnabled={() => {
          updateMcpMutation.mutate(
            { deviceId: selectedMcp.device_id, mcpId: selectedMcp.mcp_id, request: { enabled: !selectedMcp.enabled } },
            {
              onSuccess: () => toast.success(`${selectedMcp.name} ${selectedMcp.enabled ? 'disabled' : 'enabled'}`),
              onError: (err) => toast.error(err.message),
            },
          );
        }}
        onDelete={() => setMcpToDelete(selectedMcp)}
        hasDaemon={hasDaemon}
        onThisComputer={selectedMcp.on_this_computer}
        onEdit={() => setEditTarget({ mcpId: selectedMcp.mcp_id, name: selectedMcp.name })}
      />
    );
  }, [selectedMcp, mcpToolsData, deviceReachable, updateMcpMutation, controlMcpMutation, hasDaemon]);

  const list = (
    <div data-testid="tools-tab-view">
      <DataList<ToolsServerRow>
        headerLeading={
          <ListFilterMenu
            testId="tool-filters"
            filters={[
              {
                key: 'status',
                label: 'Status',
                testId: 'tool-status-filter',
                value: mcpStatusScope,
                options: TOOL_CHIPS.map((chip) => ({ value: chip.value, label: chip.label, testId: chip.testid })),
                onChange: (value) => mcpOnFilterChange('status', value),
              },
            ]}
          />
        }
        items={pagedMcpServers}
        renderItem={renderMcpItem}
        searchValue={mcpSearchValue}
        onSearchChange={mcpOnSearchChange}
        searchPlaceholder="Search MCP servers..."
        page={mcpPagination.page}
        totalPages={mcpPagination.totalPages}
        onPageChange={mcpPagination.onPageChange}
        totalItems={filteredMcpServers.length}
        pageSize={mcpPagination.pageSize}
        pageSizeOptions={mcpPagination.pageSizeOptions}
        onPageSizeChange={mcpPagination.onPageSizeChange}
        getItemId={getMcpItemId}
        onItemClick={handleMcpItemClick}
        isLoading={mcpServersLoading}
        itemActions={mcpItemActions}
        headerActions={
          <ListAddButton label="Register MCP server" testId="tool-register-button" onClick={() => setRegisterOpen(true)} />
        }
        emptyState={
          <div className="text-center">
            <p className="text-body text-muted-foreground">No MCP servers registered</p>
          </div>
        }
      />
    </div>
  );

  const dialogs = (
    <>
      <RegisterMcpDialog
        open={registerOpen}
        onOpenChange={setRegisterOpen}
        hasDaemon={hasDaemon}
        onRegistered={setSelectedMcpId}
      />
      <EditLocalMcpDialog target={editTarget} onClose={() => setEditTarget(null)} />
      <Dialog open={mcpToDelete !== null} onOpenChange={(open) => { if (!open) setMcpToDelete(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete MCP Server</DialogTitle>
            <DialogDescription>
              Delete &quot;{mcpToDelete?.name}&quot; ({mcpToDelete?.mcp_id})? This will remove all its tools from the catalog. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMcpToDelete(null)}>Cancel</Button>
            <Button
              variant="destructive"
              data-testid="mcp-delete-confirm"
              onClick={handleDeleteMcp}
              disabled={deleteMcpMutation.isPending}
            >
              {deleteMcpMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  return { list, detail, selectedId: selectedMcpId, setSelectedId: setSelectedMcpId, dialogs };
}

export function McpServersPanel() {
  const panel = useMcpServersPanel();
  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="mcp-servers-panel">
      <ListDetailLayout
        layout="panel"
        panelWidth="65%"
        selectedId={panel.selectedId}
        onSelectItem={panel.setSelectedId}
        className="flex-1 min-h-0"
        listContent={<div className="p-4">{panel.list}</div>}
        detailContent={panel.detail}
      />
      {panel.dialogs}
    </div>
  );
}
